import './Search.scss';
import React, { HTMLProps, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import { IDebounceResult } from '@/types/projects.types';
import clsx from 'clsx';
import { useDebounce } from '@hooks/useDebounce';
import Button from '@atoms/Button';

export interface ISearchProps extends HTMLProps<HTMLInputElement> {
    /** Возможность очистки поля по клику */
    onClear?: () => void;
    /** Показать иконку очистки */
    showClear?: boolean;
    /** Дебаунс */
    debounce?: number;
    /** Иконка в конце поля */
    endAdornment?: ReactNode;
    /** Обработка нажатий с эффектом debounce */
    onDebounce?: (result: IDebounceResult) => void;
    /**
     * Проверять ввод в соответствии с регулярным выражением
     * @example Для проверки на отсутствие спецсимволов в строке можно использовать `'^[\da-zA-Zа-яА-Я]*$'`
     */
    pattern?: string;
    /**
     * Является ли Search компонентом HistoryCardFilter
     * @default false
     */
    isCardFilter?: boolean;
    /** Дополнительный контент для отображения под полем поиска */
    dropdownContent?: ReactNode;
    /** Показывать ли дополнительный контент */
    showDropdown?: boolean;
    /** Обработчик клика по полю поиска */
    onInputClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
    /** Обработчик события onKeyDown */
    onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void | Promise<void>;
    /** Обработчик события клика по иконке поиска */
    onSearchIconClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
}

const Search: React.FC<ISearchProps> = ({
    onClear,
    showClear = true,
    debounce = 500,
    endAdornment,
    onDebounce = () => {},
    pattern,
    isCardFilter = false,
    dropdownContent,
    showDropdown = false,
    onInputClick,
    onInputKeyDown,
    onSearchIconClick,
    disabled,
    ...props
}: ISearchProps) => {
    // -------------------------------------------------------------------------------------------------------------------
    const mouseOnComponent = useRef(true);

    const [value, setValue] = useState<string>(props.value ? props.value.toString() : '');
    const ref = useRef<HTMLInputElement>(null);

    // Регулярное выражение для проверки ввода
    const regexp = useMemo(() => {
        if (pattern) {
            return new RegExp(pattern);
        }

        return null;
    }, [pattern]);

    // =======================

    useDebounce(value, debounce, (v) => {
        if (onDebounce) {
            onDebounce({ debounceString: v.toString() });
        }
    });

    useEffect(() => {
        setValue(props.value ? props.value.toString() : '');
    }, [props.value]);

    // -------------------------------------------------------------------------------------------------------------------

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (regexp && !regexp.test(e.target.value)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        setValue(e.target.value);
        props.onChange && props.onChange(e);
    };

    const onClearClickHandler = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        e.preventDefault();

        setValue('');
        onClear && onClear();
        onDebounce && onDebounce({ debounceString: '' });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (onInputKeyDown) {
            onInputKeyDown(e);
        }
    };

    const onClickHandler = (e: React.MouseEvent<HTMLInputElement>) => {
        if (onInputClick) {
            onInputClick(e);
        }
    };

    // -------------------------------------------------------------------------------------------------------------------

    return (
        <div
            className={clsx('rf-search', isCardFilter && 'rf-search-hcf')}
            onMouseEnter={() => {
                mouseOnComponent.current = true;
            }}
            onMouseLeave={() => {
                mouseOnComponent.current = false;
            }}
        >
            <input
                {...props}
                disabled={disabled}
                ref={ref}
                type="text"
                className={clsx('rf-search__input', endAdornment && 'rf-search__input-endAdornment')}
                placeholder={props.placeholder || 'Поиск'}
                value={value}
                data-testid="search-test-id"
                onChange={onChangeHandler}
                onClick={onClickHandler}
                pattern={pattern}
                onKeyDown={onKeyDown}
            />
            <Button
                buttonType="icon-round"
                size="xs"
                className="rf-search__search-icon"
                aria-label="Найти"
                onClick={onSearchIconClick}
                startAdornment={<MdSearch size="24" />}
            />
            <div className="rf-search__endAdornment">
                {value.length > 0 && showClear && (
                    <MdClose
                        data-testid="search-clear-test-id"
                        className={clsx('rf-search__close-icon')}
                        onMouseDown={onClearClickHandler}
                    />
                )}
                {endAdornment}
            </div>

            {showDropdown && dropdownContent}
        </div>
    );
};

export default Search;
